import axios from "axios";
import { getToken } from "../services/userService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://backend-dot-immersely-ai.nw.r.appspot.com/api/v1";

/**
 * Create timestamped folder name in format YYYY-MM-DD_HH-mm-ss
 * @returns {string} Timestamped folder name
 */
export function createTimestampedFolderName() {
  const now = new Date();
  // Use UTC so admin evidence matching can compare folder names to sessionStart reliably.
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");
  const day = String(now.getUTCDate()).padStart(2, "0");
  const hours = String(now.getUTCHours()).padStart(2, "0");
  const minutes = String(now.getUTCMinutes()).padStart(2, "0");
  const seconds = String(now.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

/**
 * Format emotion data as JSON Lines (one JSON object per line)
 * @param {Array} emotionEvents - Array of emotion event objects
 * @returns {string} Formatted JSON Lines string
 */
export function formatEmotionData(emotionEvents) {
  if (!Array.isArray(emotionEvents) || emotionEvents.length === 0) {
    return "";
  }

  return emotionEvents
    .map((event) => {
      if (typeof event === "string") {
        // Already formatted as JSON string
        return event;
      }
      // Format as JSON object
      return JSON.stringify({
        arousal: event.arousal,
        valence: event.valence,
        timestamp: event.timestamp || new Date().toISOString(),
      });
    })
    .join("\n");
}

/**
 * Get presigned URL for GCP upload
 * @param {string} fileName - Full file path including folder
 * @param {number} fileSize - File size in bytes
 * @param {string} mimeType - MIME type of the file
 * @returns {Promise<string>} Presigned URL
 */
export async function getPresignedUrl(fileName, fileSize, mimeType) {
  try {
    const response = await axios.get(`${API_BASE_URL}/preSignedUrl`, {
      params: {
        key: fileName,
        size: fileSize,
        mimetype: mimeType,
      },
      headers: {
        "Authorization": `Bearer ${getToken()}`,
      },
    });

    return response.data.url;
  } catch (error) {
    console.error("Error getting presigned URL:", error);
    throw error;
  }
}

/**
 * Upload file to GCP using presigned URL
 * @param {Blob} fileBlob - File blob to upload
 * @param {string} fileName - File name (will be prefixed with folderPath)
 * @param {string} folderPath - Folder path (e.g., "videos/username/timestamp")
 * @param {string} mimeType - MIME type of the file
 * @returns {Promise<Object>} Upload result
 */
export async function uploadToGCP(fileBlob, fileName, folderPath, mimeType) {
  try {
    const fullFileName = `${folderPath}/${fileName}`;
    const fileSize = fileBlob.size;

    // Get presigned URL
    const presignedUrl = await getPresignedUrl(fullFileName, fileSize, mimeType);

    // Upload file using PUT request
    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      body: fileBlob,
      headers: {
        "Content-Type": mimeType,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    return {
      success: true,
      fileName: fullFileName,
      url: presignedUrl.split("?")[0], // Remove query params for clean URL
    };
  } catch (error) {
    console.error("Error uploading to GCP:", error);
    throw error;
  }
}

/**
 * Upload text file using multipart form data (for face_annotation.txt)
 * @param {string} content - Text content to upload
 * @param {string} fileName - File name
 * @param {string} folderPath - Folder path
 * @returns {Promise<Object>} Upload result
 */
export async function uploadTextFile(content, fileName, folderPath) {
  try {
    const formData = new FormData();
    const blob = new File([content], fileName, { type: "text/plain" });
    
    formData.append("file", blob);
    formData.append("bucket", process.env.REACT_APP_GCP_BUCKET_NAME || "immersely-bucket");
    formData.append("folder", folderPath);

    const response = await axios.post(
      `${API_BASE_URL}/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${getToken()}`,
        },
      }
    );

    return {
      success: true,
      fileName: `${folderPath}/${fileName}`,
      data: response.data,
    };
  } catch (error) {
    console.error("Error uploading text file:", error);
    throw error;
  }
}

export function sampleStillFileName(kind, seconds) {
  return `samples/${kind}_s${String(Math.round(Math.max(0, seconds))).padStart(5, "0")}.jpg`;
}

export async function captureJpegFromStream(stream, quality = 0.72) {
  if (!stream) return null;
  const track = stream.getVideoTracks?.()[0];
  if (!track || track.readyState !== "live") return null;

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;

  try {
    await video.play();
  } catch {
    // Autoplay can fail; loadeddata is enough to draw a frame.
  }

  if (!video.videoWidth) {
    await new Promise((resolve) => {
      const onReady = () => {
        video.removeEventListener("loadeddata", onReady);
        resolve();
      };
      video.addEventListener("loadeddata", onReady);
      setTimeout(resolve, 400);
    });
  }

  const width = video.videoWidth || 640;
  const height = video.videoHeight || 360;
  const canvas = document.createElement("canvas");
  canvas.width = Math.min(width, 854);
  canvas.height = Math.max(1, Math.round(canvas.width * (height / Math.max(width, 1))));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  video.srcObject = null;
  video.remove();

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
  });
}

const REVIEW_STILL_FRACTIONS = [0, 0.3, 0.5, 0.7, 0.9, 0.98];

export function pickReviewStills(captures, durationSeconds) {
  if (!Array.isArray(captures) || captures.length === 0) {
    return [];
  }

  const duration = Math.max(
    Number(durationSeconds) || 0,
    captures[captures.length - 1].seconds || 0,
    1,
  );
  const used = new Set();

  return REVIEW_STILL_FRACTIONS.map((fraction) => {
    const target = duration * fraction;
    let bestIndex = -1;
    let bestDistance = Infinity;
    captures.forEach((item, index) => {
      if (used.has(index)) return;
      const distance = Math.abs((item.seconds || 0) - target);
      if (distance < bestDistance) {
        bestIndex = index;
        bestDistance = distance;
      }
    });
    if (bestIndex === -1) return null;
    used.add(bestIndex);
    return {
      ...captures[bestIndex],
      targetSeconds: target,
    };
  }).filter(Boolean);
}

/**
 * Chrome MediaRecorder writes WebM Duration as 0 when using a timeslice, so
 * players and the review stills worker treat a long recording as much shorter.
 * Patch the header float (milliseconds) without copying the rest of the file.
 */
export async function withFixedWebmDuration(blob, durationMs) {
  if (!blob || blob.size < 64 || !(durationMs > 1000)) return blob;
  const type = blob.type || "";
  if (type && !type.includes("webm")) return blob;

  const headerLen = Math.min(blob.size, 65536);
  const header = new Uint8Array(await blob.slice(0, headerLen).arrayBuffer());
  const view = new DataView(header.buffer);
  let patched = false;
  for (let i = 0; i < header.length - 12; i += 1) {
    if (header[i] !== 0x44 || header[i + 1] !== 0x89 || header[i + 2] !== 0x88) continue;
    const current = view.getFloat64(i + 3, false);
    if (Number.isFinite(current) && current > 1000) return blob;
    view.setFloat64(i + 3, durationMs, false);
    patched = true;
    break;
  }
  if (!patched) return blob;
  return new Blob([header, blob.slice(headerLen)], { type: type || "video/webm" });
}









